import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DownloadableGuideSection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const guides = [
    {
      title: "Guia Completo de Marcenaria Artesanal",
      description: "Descubra os segredos da marcenaria tradicional brasileira, técnicas de acabamento e como escolher móveis de qualidade.",
      pages: "48 páginas",
      format: "PDF",
      size: "12 MB",
      image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      topics: [
        "História da marcenaria brasileira",
        "Tipos de madeira e suas características",
        "Técnicas de acabamento artesanal",
        "Como identificar móveis de qualidade",
        "Cuidados e manutenção",
        "Tendências em design de móveis"
      ]
    },
    {
      title: "Catálogo de Madeiras Nobres",
      description: "Conheça as principais madeiras utilizadas em nossos móveis, suas origens, características e aplicações ideais.",
      pages: "32 páginas",
      format: "PDF",
      size: "18 MB",
      image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      topics: [
        "Madeiras brasileiras certificadas",
        "Características de cada espécie",
        "Aplicações recomendadas",
        "Sustentabilidade e certificações",
        "Cuidados específicos por tipo",
        "Galeria de texturas e cores"
      ]
    },
    {
      title: "Manual de Design de Interiores",
      description: "Dicas profissionais para harmonizar móveis artesanais com diferentes estilos de decoração e arquitetura.",
      pages: "56 páginas",
      format: "PDF",
      size: "15 MB",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
      topics: [
        "Princípios de design de interiores",
        "Combinação de estilos",
        "Planejamento de ambientes",
        "Iluminação para móveis de madeira",
        "Paleta de cores harmoniosas",
        "Casos de sucesso reais"
      ]
    }
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (email) {
      setIsSubmitted(true);
      // Simulate download process
      setTimeout(() => {
        setIsSubmitted(false);
        setEmail('');
      }, 3000);
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-secondary/10 text-secondary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Download" size={16} />
            <span>Materiais Gratuitos</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-6">
            Guias Exclusivos
            <span className="block text-secondary">de Marcenaria</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Baixe gratuitamente nossos guias especializados e aprenda sobre marcenaria artesanal, 
            madeiras nobres e design de interiores com móveis únicos.
          </p>
        </div>

        {/* Guides Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {guides?.map((guide, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-muted/30 to-white rounded-3xl shadow-premium border border-border hover:shadow-premium-lg transition-all duration-300 overflow-hidden group"
            >
              {/* Guide Image */}
              <div className="relative overflow-hidden">
                <Image
                  src={guide?.image}
                  alt={guide?.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {/* Máscara preta com transparência uniforme */}
                <div className="absolute inset-0 bg-black/30"></div>
                {/* Gradiente adicional para melhor contraste */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent"></div>
                
                {/* File Info Badge */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-premium">
                  <div className="flex items-center space-x-2">
                    <Icon name="FileText" size={16} color="var(--color-primary)" />
                    <span className="text-xs font-medium text-primary">{guide?.format}</span>
                  </div>
                </div>
              </div>

              {/* Guide Content */}
              <div className="p-8">
                <div className="space-y-6">
                  <div>
                    <h3 className="font-playfair text-xl font-bold text-primary mb-3">
                      {guide?.title}
                    </h3>
                    <p className="text-text-secondary leading-relaxed mb-4">
                      {guide?.description}
                    </p>
                    
                    {/* File Details */}
                    <div className="flex items-center space-x-4 text-sm text-text-secondary">
                      <div className="flex items-center space-x-1">
                        <Icon name="FileText" size={14} />
                        <span>{guide?.pages}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Icon name="HardDrive" size={14} />
                        <span>{guide?.size}</span>
                      </div>
                    </div>
                  </div>

                  {/* Topics */}
                  <div>
                    <h4 className="font-medium text-primary mb-3">Conteúdo Incluído:</h4>
                    <ul className="space-y-2">
                      {guide?.topics?.slice(0, 4)?.map((topic, topicIndex) => (
                        <li key={topicIndex} className="flex items-start space-x-2">
                          <Icon name="Check" size={14} color="var(--color-success)" className="mt-1 flex-shrink-0" />
                          <span className="text-sm text-text-secondary">{topic}</span>
                        </li>
                      ))}
                      {guide?.topics?.length > 4 && (
                        <li className="text-sm text-accent font-medium">
                          +{guide?.topics?.length - 4} tópicos adicionais
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Download Form */}
        <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl p-12">
          <div className="max-w-2xl mx-auto text-center">
            {!isSubmitted ? (
              <>
                <Icon name="Gift" size={48} color="var(--color-accent)" className="mx-auto mb-6" />
                <h3 className="font-playfair text-3xl font-bold text-primary mb-4">
                  Baixe Todos os Guias Gratuitamente
                </h3>
                <p className="text-text-secondary mb-8 leading-relaxed">
                  Insira seu e-mail abaixo e receba instantaneamente nossos 3 guias exclusivos 
                  sobre marcenaria artesanal, além de dicas e novidades da PH Móveis.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="max-w-md mx-auto">
                    <Input
                      type="email"
                      placeholder="Seu melhor e-mail"
                      value={email}
                      onChange={(e) => setEmail(e?.target?.value)}
                      required
                      className="text-center"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    iconName="Download"
                    iconPosition="left"
                    className="bg-gradient-forest hover:shadow-forest"
                    disabled={!email}
                  >
                    Baixar Guias Gratuitos
                  </Button>
                </form>

                <p className="text-xs text-text-secondary mt-4">
                  Não enviamos spam. Seus dados estão seguros conosco.
                </p>
              </>
            ) : (
              <div className="space-y-6">
                <div className="w-16 h-16 bg-success rounded-full flex items-center justify-center mx-auto">
                  <Icon name="CheckCircle" size={32} color="white" />
                </div>
                <h3 className="font-playfair text-2xl font-bold text-primary">
                  Download Iniciado!
                </h3>
                <p className="text-text-secondary">
                  Verifique sua caixa de entrada. Os guias foram enviados para seu e-mail.
                </p>
                <div className="flex justify-center space-x-4">
                  <Button
                    variant="outline"
                    iconName="Mail"
                    iconPosition="left"
                    className="border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    Abrir E-mail
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional Benefits */}
        <div className="mt-16 grid md:grid-cols-3 gap-8 text-center">
          <div className="space-y-4">
            <div className="w-12 h-12 bg-gradient-forest rounded-xl flex items-center justify-center mx-auto">
              <Icon name="BookOpen" size={24} color="white" />
            </div>
            <h4 className="font-playfair text-lg font-bold text-primary">Conhecimento Especializado</h4>
            <p className="text-text-secondary text-sm">
              Conteúdo criado por nossos mestres artesãos com 25+ anos de experiência
            </p>
          </div>

          <div className="space-y-4">
            <div className="w-12 h-12 bg-gradient-forest rounded-xl flex items-center justify-center mx-auto">
              <Icon name="Smartphone" size={24} color="white" />
            </div>
            <h4 className="font-playfair text-lg font-bold text-primary">Acesso Mobile</h4>
            <p className="text-text-secondary text-sm">
              PDFs otimizados para leitura em qualquer dispositivo, a qualquer hora
            </p>
          </div>

          <div className="space-y-4">
            <div className="w-12 h-12 bg-gradient-forest rounded-xl flex items-center justify-center mx-auto">
              <Icon name="RefreshCw" size={24} color="white" />
            </div>
            <h4 className="font-playfair text-lg font-bold text-primary">Atualizações Gratuitas</h4>
            <p className="text-text-secondary text-sm">
              Receba novas versões e conteúdos adicionais sem custo adicional
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DownloadableGuideSection;